import type { MyPermissionsResponse } from '@/types/api'
import { api } from '@/lib/api'

const STORAGE_KEY = 'sd2_workspace_my_permissions'

export const OWNER_KNOWLEDGE_BASE_PERMISSIONS = [
  'access_knowledge_base',
  'manage_knowledge_base',
] as const

export function isWorkspaceOwner(role: string | undefined | null): boolean {
  return (role || '').toLowerCase() === 'owner'
}

export function isWorkspaceAdmin(role: string | undefined | null): boolean {
  const r = (role || '').toLowerCase()
  return r === 'admin' || r === 'owner'
}

function applyOwnerKnowledgeBasePermissions(
  permissions: Record<string, boolean>,
): Record<string, boolean> {
  const next = { ...permissions }
  for (const key of OWNER_KNOWLEDGE_BASE_PERMISSIONS) {
    next[key] = true
  }
  return next
}

function empty(): MyPermissionsResponse {
  return { role: '', permissions: {}, logic_flags: {}, can_manage_permissions: false }
}

/** Fallback как в `frontend/src/utils/rbac.js` при ошибке API */
export function defaultPermissionsForRole(role: string | undefined): MyPermissionsResponse {
  const r = (role || '').toLowerCase()
  if (r === 'owner' || r === 'admin') {
    return {
      role: r,
      can_manage_permissions: true,
      logic_flags: {},
      permissions: {
        access_analytics: true,
        access_tickets: true,
        access_ai_tickets: true,
        access_reviews: true,
        access_quality_arm: true,
        access_debug: true,
        access_exports: true,
        access_users: true,
        access_operators: true,
        access_schedules_plan: true,
        access_schedules_actual: true,
        access_schedules: true,
        access_settings: true,
        access_installers: true,
        access_anomalies: true,
        access_actualization: true,
        access_knowledge_base: true,
        manage_knowledge_base: true,
        access_activity_log: true,
        access_ai_audit_log: true,
        manage_workspace_settings: true,
        manage_quality_criteria_defaults: true,
        manage_projects: true,
        manage_operator_routing: true,
        manage_triggers: true,
        manage_role_permissions: true,
        manage_operator_emails: true,
        access_operator_quick_settings: true,
        access_schedule_staffing_analysis: true,
      },
    }
  }
  const operatorLike = ['operator', 'trainee', 'shift_lead', 'npc_helper', 'human_helper', 'analyst']
  if (operatorLike.includes(r)) {
    return {
      role: r,
      can_manage_permissions: false,
      logic_flags: {},
      permissions: {
        access_analytics: true,
        access_tickets: true,
        access_ai_tickets: true,
        access_reviews: true,
        access_quality_arm: r === 'shift_lead' || r === 'analyst',
        access_debug: false,
        access_exports: r === 'shift_lead' || r === 'human_helper' || r === 'analyst',
        access_users: true,
        access_operators: true,
        access_schedules_plan: true,
        access_schedules_actual: true,
        access_schedules: true,
        access_settings: true,
        access_installers: true,
        access_anomalies: true,
        access_actualization: false,
        access_knowledge_base: true,
        manage_knowledge_base: false,
        access_activity_log: true,
        access_ai_audit_log: false,
        manage_workspace_settings: false,
        manage_quality_criteria_defaults: false,
        manage_projects: false,
        manage_operator_routing: false,
        manage_triggers: false,
        manage_role_permissions: false,
        manage_operator_emails: false,
        access_operator_quick_settings: true,
        access_schedule_staffing_analysis: true,
      },
    }
  }
  if (r === 'viewer') {
    return {
      role: r,
      can_manage_permissions: false,
      logic_flags: {},
      permissions: {
        access_analytics: true,
        access_tickets: true,
        access_ai_tickets: true,
        access_reviews: false,
        access_quality_arm: false,
        access_debug: false,
        access_exports: false,
        access_users: true,
        access_operators: true,
        access_schedules_plan: false,
        access_schedules_actual: false,
        access_schedules: false,
        access_settings: false,
        access_installers: false,
        access_anomalies: false,
        access_actualization: false,
        access_knowledge_base: true,
        access_activity_log: true,
        access_ai_audit_log: false,
        manage_workspace_settings: false,
        manage_quality_criteria_defaults: false,
        manage_projects: false,
        manage_operator_routing: false,
        manage_triggers: false,
        manage_role_permissions: false,
        manage_operator_emails: false,
        access_operator_quick_settings: false,
        access_schedule_staffing_analysis: false,
      },
    }
  }
  return empty()
}

function asBoolRecord(v: unknown): Record<string, boolean> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  return v as Record<string, boolean>
}

/** Дополняет ответ API дефолтами роли, если в матрице не хватает ключей. */
export function mergeWithDefaultPermissions(
  data: MyPermissionsResponse | null | undefined,
  roleFallback?: string,
): MyPermissionsResponse {
  const fb = defaultPermissionsForRole(roleFallback)
  if (!data) return fb
  const dp = asBoolRecord(data.permissions) ?? {}
  const lf = asBoolRecord(data.logic_flags) ?? {}
  let permissions = { ...fb.permissions, ...dp }
  if (isWorkspaceOwner(roleFallback)) {
    permissions = applyOwnerKnowledgeBasePermissions(permissions)
  }
  return {
    role: data.role || fb.role,
    can_manage_permissions: data.can_manage_permissions ?? fb.can_manage_permissions,
    permissions,
    logic_flags: { ...fb.logic_flags, ...lf },
  }
}

export function readCachedPermissions(): MyPermissionsResponse | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as MyPermissionsResponse
  } catch {
    /* ignore */
  }
  return null
}

export function writeCachedPermissions(data: MyPermissionsResponse) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    /* ignore */
  }
}

export async function loadMyPermissions(roleFallback?: string): Promise<MyPermissionsResponse> {
  try {
    const data = await api.myPermissions()
    writeCachedPermissions(data)
    return data
  } catch {
    const fb = defaultPermissionsForRole(roleFallback)
    writeCachedPermissions(fb)
    return fb
  }
}

export function hasPermission(
  data: MyPermissionsResponse | null | undefined,
  key: string,
  accountRole?: string | null,
): boolean {
  if (isWorkspaceOwner(accountRole) && (OWNER_KNOWLEDGE_BASE_PERMISSIONS as readonly string[]).includes(key)) {
    return true
  }
  const perms = data?.permissions
  if (!perms) return false
  if (key === 'access_analytics') {
    if (perms.access_analytics != null) return !!perms.access_analytics
    return !!(perms as Record<string, boolean>).access_dashboard
  }
  return !!perms[key]
}

/** Устаревшее `access_schedules` даёт оба новых права, пока матрица не пересохранена. */
export function hasSchedulePlanAccess(data: MyPermissionsResponse | null | undefined): boolean {
  return (
    hasPermission(data, 'access_schedules_plan') ||
    hasPermission(data, 'access_schedules')
  )
}

export function hasScheduleActualAccess(data: MyPermissionsResponse | null | undefined): boolean {
  return (
    hasPermission(data, 'access_schedules_actual') ||
    hasPermission(data, 'access_schedules')
  )
}

export function canAccessSchedulesNavigation(data: MyPermissionsResponse | null | undefined): boolean {
  return hasSchedulePlanAccess(data) || hasScheduleActualAccess(data)
}

/** Плановый график + право на страницу AI-анализа табеля. */
export function canAccessScheduleStaffingAnalysis(data: MyPermissionsResponse | null | undefined): boolean {
  return hasSchedulePlanAccess(data) && hasPermission(data, 'access_schedule_staffing_analysis')
}

/** Быстрые настройки проекта или полное управление проектами. */
export function canAccessOperatorQuickSettings(data: MyPermissionsResponse | null | undefined): boolean {
  return (
    hasPermission(data, 'access_operator_quick_settings') || hasPermission(data, 'manage_projects')
  )
}

/** Настройки проектов: вкладка «Проекты» на /settings и меню «⋯» у проекта в сайдбаре. */
export function canAccessProjectSettings(data: MyPermissionsResponse | null | undefined): boolean {
  return hasPermission(data, 'manage_projects')
}

/** Настройка автораспределения обращений по операторам и проектам. */
export function canManageOperatorRouting(data: MyPermissionsResponse | null | undefined): boolean {
  return hasPermission(data, 'manage_operator_routing') || hasPermission(data, 'manage_projects')
}

/** Просмотр базы знаний — по умолчанию для всех сотрудников (кроме customer). */
export function canAccessKnowledgeBase(
  data: MyPermissionsResponse | null | undefined,
  accountRole?: string | null,
): boolean {
  const r = (accountRole || data?.role || '').toLowerCase()
  if (r === 'customer') return false
  if (isWorkspaceOwner(accountRole)) return true
  const perms = data?.permissions
  if (perms && Object.prototype.hasOwnProperty.call(perms, 'access_knowledge_base')) {
    return !!perms.access_knowledge_base
  }
  return true
}

/** Добавление материалов базы знаний — только admin/owner с manage_knowledge_base. */
export function canAddKnowledgeBase(
  data: MyPermissionsResponse | null | undefined,
  accountRole?: string | null,
): boolean {
  return isWorkspaceAdmin(accountRole) && hasPermission(data, 'manage_knowledge_base', accountRole)
}

/** Редактирование материалов базы знаний — только admin/owner с manage_knowledge_base. */
export function canEditKnowledgeBase(
  data: MyPermissionsResponse | null | undefined,
  accountRole?: string | null,
): boolean {
  return canAddKnowledgeBase(data, accountRole)
}

/** Удаление материалов базы знаний — только admin/owner с manage_knowledge_base. */
export function canDeleteKnowledgeBase(
  data: MyPermissionsResponse | null | undefined,
  accountRole?: string | null,
): boolean {
  return canAddKnowledgeBase(data, accountRole)
}

/** Любое управление материалами базы знаний — только admin/owner с manage_knowledge_base. */
export function canManageKnowledgeBase(
  data: MyPermissionsResponse | null | undefined,
  accountRole?: string | null,
): boolean {
  return canAddKnowledgeBase(data, accountRole)
}
